/**
 * @file Schedule Manager Page — /mi-horario
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Ban, CalendarCheck, Info } from "lucide-react";
import { useSchedule } from "../hooks/useSchedule";
import { useScheduleOverride } from "../hooks/useScheduleOverride";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { useProfessionalLocations } from "../hooks/useProfessionalLocations";
import { SCHEDULE_STRINGS, SCHEDULE_OVERRIDE_STRINGS } from "../constants/strings";
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  Input,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../ui";
import "./ScheduleManager.css";

const calcCapacity = (startTime, endTime, sessionDuration, bufferTime) => {
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  const totalMinutes = eh * 60 + em - (sh * 60 + sm);
  const slotSize = (sessionDuration || 60) + (bufferTime || 0);
  return slotSize > 0 ? Math.floor(totalMinutes / slotSize) : 0;
};

const DAYS = SCHEDULE_STRINGS.DAYS.map((label, value) => ({ value, label }));

const _now = new Date();
const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;

export default function ScheduleManager() {
  const navigate = useNavigate();
  const { schedules, isLoading, createSlot, updateSlot, deleteSlot } = useSchedule();
  const { profile, updateProfile } = useProfessionalProfile();
  const { locations } = useProfessionalLocations();
  const {
    overrides,
    isLoading: overridesLoading,
    createOverride,
    deleteOverride,
  } = useScheduleOverride();

  const [tab, setTab] = useState("weekly");

  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [slotForm, setSlotForm] = useState({ dayOfWeek: 1, startTime: "09:00", endTime: "17:00", locationId: "" });
  const [slotSubmitting, setSlotSubmitting] = useState(false);

  const [editSlot, setEditSlot] = useState(null);
  const [editForm, setEditForm] = useState({ startTime: "", endTime: "", locationId: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    date: "",
    isBlocked: true,
    startTime: "09:00",
    endTime: "17:00",
    reason: "",
  });
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  const [overlapInfo, setOverlapInfo] = useState(null);

  const findOverlaps = (form) =>
    schedules.filter(
      (s) => s.dayOfWeek === form.dayOfWeek && form.startTime < s.endTime && form.endTime > s.startTime
    );

  const computeUnion = (existing, newSlot) => {
    const starts = [existing.startTime, newSlot.startTime].sort();
    const ends = [existing.endTime, newSlot.endTime].sort();
    return { unionStart: starts[0], unionEnd: ends[ends.length - 1] };
  };

  const handleCreateSlot = async () => {
    if (locations.length > 0 && !slotForm.locationId) return;
    const overlaps = findOverlaps(slotForm);
    if (overlaps.length > 0 && !overlapInfo) {
      const existing = overlaps[0];
      const { unionStart, unionEnd } = computeUnion(existing, slotForm);
      setOverlapInfo({ existing, newSlot: { ...slotForm }, unionStart, unionEnd });
      return;
    }
    try {
      setSlotSubmitting(true);
      await createSlot({ ...slotForm, locationId: slotForm.locationId || null });
      setSlotDialogOpen(false);
      setOverlapInfo(null);
      setSlotForm({ dayOfWeek: 1, startTime: "09:00", endTime: "17:00", locationId: "" });
    } catch {
      // ignore
    } finally {
      setSlotSubmitting(false);
    }
  };

  const handleOverlapUnion = async () => {
    try {
      setSlotSubmitting(true);
      await deleteSlot(overlapInfo.existing.id);
      await createSlot({
        dayOfWeek: overlapInfo.existing.dayOfWeek,
        startTime: overlapInfo.unionStart,
        endTime: overlapInfo.unionEnd,
      });
      setOverlapInfo(null);
      setSlotDialogOpen(false);
      setSlotForm({ dayOfWeek: 1, startTime: "09:00", endTime: "17:00", locationId: "" });
    } catch {
      // ignore
    } finally {
      setSlotSubmitting(false);
    }
  };

  const handleOverlapReplace = async () => {
    try {
      setSlotSubmitting(true);
      await deleteSlot(overlapInfo.existing.id);
      await createSlot(overlapInfo.newSlot);
      setOverlapInfo(null);
      setSlotDialogOpen(false);
      setSlotForm({ dayOfWeek: 1, startTime: "09:00", endTime: "17:00", locationId: "" });
    } catch {
      // ignore
    } finally {
      setSlotSubmitting(false);
    }
  };

  const handleEditSlot = (slot) => {
    setEditSlot(slot);
    setEditForm({ startTime: slot.startTime, endTime: slot.endTime, locationId: slot.locationId || "" });
  };

  const handleSaveEdit = async () => {
    try {
      setEditSubmitting(true);
      await updateSlot(editSlot.id, { ...editForm, locationId: editForm.locationId || null });
      setEditSlot(null);
    } catch {
      // ignore
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCreateOverride = async () => {
    try {
      setOverrideSubmitting(true);
      await createOverride({
        date: overrideForm.date,
        isBlocked: overrideForm.isBlocked,
        startTime: overrideForm.isBlocked ? null : overrideForm.startTime,
        endTime: overrideForm.isBlocked ? null : overrideForm.endTime,
        reason: overrideForm.reason || null,
      });
      setOverrideDialogOpen(false);
      setOverrideForm({ date: "", isBlocked: true, startTime: "09:00", endTime: "17:00", reason: "" });
    } catch {
      // ignore
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
  };

  if (isLoading || overridesLoading) {
    return (
      <div className="sm-page">
        <div className="sm-loading">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  return (
    <div className="sm-page">
      <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate("/mi-perfil")}>
        Mi perfil
      </Button>

      <header>
        <h1 className="sm-title">{SCHEDULE_STRINGS.TITLE}</h1>
      </header>

      <Tabs value={tab} onValueChange={setTab} variant="underline">
        <TabsList>
          <TabsTrigger value="weekly">{SCHEDULE_STRINGS.TAB_WEEKLY}</TabsTrigger>
          <TabsTrigger value="overrides">{SCHEDULE_STRINGS.TAB_OVERRIDES}</TabsTrigger>
        </TabsList>

        <TabsContent value="weekly">
          {locations.some((l) => l.isVirtual) && (
            <div className="auth-alert auth-alert-info" style={{ marginBottom: 12 }}>
              <Info size={14} />
              <span>
                ¿Cuándo puede agendarse teleconsulta? Por defecto, los pacientes pueden reservar videollamada durante cualquier bloque, incluso uno asignado a un consultorio. Si prefieres restringirlo, activa la opción más abajo.
              </span>
            </div>
          )}
          <div className="sm-toolbar">
            <div className="sm-switches">
              <Switch
                label="Permitir citas el mismo día"
                checked={!!profile?.allowSameDayBooking}
                onCheckedChange={(c) => updateProfile({ allowSameDayBooking: !!c })}
              />
              {locations.some((l) => l.isVirtual) && (
                <Switch
                  label="Teleconsulta solo en bloques designados"
                  checked={!!profile?.teleconsultaOnlyInDesignatedSlots}
                  onCheckedChange={(c) => updateProfile({ teleconsultaOnlyInDesignatedSlots: !!c })}
                />
              )}
            </div>
            <Button leftIcon={<Plus size={14} />} onClick={() => setSlotDialogOpen(true)}>
              {SCHEDULE_STRINGS.ADD_SLOT}
            </Button>
          </div>

          {schedules.length === 0 ? (
            <Card className="sm-empty">
              <p>{SCHEDULE_STRINGS.EMPTY}</p>
            </Card>
          ) : (
            <Card padded={false} style={{ overflow: "hidden" }}>
              <Table>
                <THead>
                  <TR>
                    <TH>{SCHEDULE_STRINGS.DAY_OF_WEEK}</TH>
                    <TH>{SCHEDULE_STRINGS.START_TIME}</TH>
                    <TH>{SCHEDULE_STRINGS.END_TIME}</TH>
                    {locations.length > 0 && <TH>Ubicación</TH>}
                    <TH>Capacidad</TH>
                    <TH align="right" />
                  </TR>
                </THead>
                <TBody>
                  {schedules.map((slot) => (
                    <TR
                      key={slot.id}
                      onClick={() => handleEditSlot(slot)}
                      style={{ cursor: "pointer" }}
                    >
                      <TD>{SCHEDULE_STRINGS.DAYS[slot.dayOfWeek]}</TD>
                      <TD>{slot.startTime}</TD>
                      <TD>{slot.endTime}</TD>
                      {locations.length > 0 && (
                        <TD>
                          {slot.locationId
                            ? locations.find((l) => l.id === slot.locationId)?.name ||
                              locations.find((l) => l.id === slot.locationId)?.address ||
                              "—"
                            : "—"}
                        </TD>
                      )}
                      <TD>
                        <Chip size="sm">
                          {calcCapacity(
                            slot.startTime,
                            slot.endTime,
                            profile?.sessionDuration,
                            profile?.bufferTime
                          )}{" "}
                          citas
                        </Chip>
                      </TD>
                      <TD align="right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSlot(slot.id).catch(() => {});
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="overrides">
          <div className="sm-toolbar">
            <div />
            <Button leftIcon={<Plus size={14} />} onClick={() => setOverrideDialogOpen(true)}>
              {SCHEDULE_OVERRIDE_STRINGS.ADD}
            </Button>
          </div>
          {overrides.length === 0 ? (
            <Card className="sm-empty">
              <p>{SCHEDULE_OVERRIDE_STRINGS.EMPTY}</p>
            </Card>
          ) : (
            <Card padded={false} style={{ overflow: "hidden" }}>
              <Table>
                <THead>
                  <TR>
                    <TH>{SCHEDULE_OVERRIDE_STRINGS.DATE}</TH>
                    <TH>{SCHEDULE_OVERRIDE_STRINGS.TYPE}</TH>
                    <TH>{SCHEDULE_STRINGS.START_TIME}</TH>
                    <TH>{SCHEDULE_STRINGS.END_TIME}</TH>
                    <TH>{SCHEDULE_OVERRIDE_STRINGS.REASON}</TH>
                    <TH align="right" />
                  </TR>
                </THead>
                <TBody>
                  {overrides.map((ov) => (
                    <TR key={ov.id}>
                      <TD>{formatDate(ov.date)}</TD>
                      <TD>
                        {ov.isBlocked ? (
                          <Chip tone="err" size="sm" leftIcon={<Ban size={12} />}>
                            {SCHEDULE_OVERRIDE_STRINGS.BLOCKED}
                          </Chip>
                        ) : (
                          <Chip tone="primary" size="sm" leftIcon={<CalendarCheck size={12} />}>
                            {SCHEDULE_OVERRIDE_STRINGS.SPECIAL_HOURS}
                          </Chip>
                        )}
                      </TD>
                      <TD>{ov.isBlocked ? "—" : ov.startTime}</TD>
                      <TD>{ov.isBlocked ? "—" : ov.endTime}</TD>
                      <TD>{ov.reason || "—"}</TD>
                      <TD align="right">
                        <Button variant="ghost" size="sm" onClick={() => deleteOverride(ov.id).catch(() => {})}>
                          <Trash2 size={14} />
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!overlapInfo} onOpenChange={(v) => !v && setOverlapInfo(null)}>
        <DialogContent size="sm" title="Horario con traslape">
          {overlapInfo && (
            <DialogBody>
              <p style={{ margin: "0 0 12px", fontSize: 14 }}>
                Ya tienes un horario el <strong>{SCHEDULE_STRINGS.DAYS[overlapInfo.existing.dayOfWeek]}</strong> de{" "}
                <strong>{overlapInfo.existing.startTime}</strong> a <strong>{overlapInfo.existing.endTime}</strong>, y
                el nuevo (<strong>{overlapInfo.newSlot.startTime}</strong> a{" "}
                <strong>{overlapInfo.newSlot.endTime}</strong>) se cruza con él.
              </p>
              <p style={{ margin: "0 0 12px", color: "var(--ink-3)", fontSize: 13 }}>¿Qué te gustaría hacer?</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Button variant="outline" onClick={handleOverlapUnion} loading={slotSubmitting}>
                  Combinar ambos ({overlapInfo.unionStart} – {overlapInfo.unionEnd})
                </Button>
                <Button variant="outline" onClick={handleOverlapReplace} loading={slotSubmitting}>
                  Reemplazar el anterior con el nuevo
                </Button>
              </div>
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverlapInfo(null)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSlot} onOpenChange={(v) => !v && setEditSlot(null)}>
        <DialogContent
          size="sm"
          title={`Editar horario: ${editSlot && SCHEDULE_STRINGS.DAYS[editSlot.dayOfWeek]}`}
        >
          <DialogBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Input
                  type="time"
                  label={SCHEDULE_STRINGS.START_TIME}
                  value={editForm.startTime}
                  onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                />
                <Input
                  type="time"
                  label={SCHEDULE_STRINGS.END_TIME}
                  value={editForm.endTime}
                  onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                />
              </div>
              {locations.length > 0 && (
                <Select
                  label="Ubicación"
                  value={editForm.locationId ? String(editForm.locationId) : ""}
                  onValueChange={(v) =>
                    setEditForm({ ...editForm, locationId: v === "__none__" ? "" : v })
                  }
                  placeholder="Sin ubicación asignada"
                >
                  <SelectItem value="__none__">Sin ubicación asignada</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={String(loc.id)}>
                      {loc.name || loc.address}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditSlot(null)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} loading={editSubmitting}>
              {SCHEDULE_STRINGS.SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={slotDialogOpen} onOpenChange={setSlotDialogOpen}>
        <DialogContent size="sm" title={SCHEDULE_STRINGS.ADD_SLOT}>
          <DialogBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Select
                label={SCHEDULE_STRINGS.DAY_OF_WEEK}
                value={String(slotForm.dayOfWeek)}
                onValueChange={(v) => setSlotForm({ ...slotForm, dayOfWeek: Number(v) })}
              >
                {DAYS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>
                    {d.label}
                  </SelectItem>
                ))}
              </Select>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Input
                  type="time"
                  label={SCHEDULE_STRINGS.START_TIME}
                  value={slotForm.startTime}
                  onChange={(e) => setSlotForm({ ...slotForm, startTime: e.target.value })}
                />
                <Input
                  type="time"
                  label={SCHEDULE_STRINGS.END_TIME}
                  value={slotForm.endTime}
                  onChange={(e) => setSlotForm({ ...slotForm, endTime: e.target.value })}
                />
              </div>
              {locations.length > 0 && (
                <Select
                  label="Ubicación"
                  value={slotForm.locationId ? String(slotForm.locationId) : ""}
                  onValueChange={(v) => setSlotForm({ ...slotForm, locationId: v })}
                  placeholder="Selecciona una ubicación"
                  hint="Selecciona el consultorio o teleconsulta para este bloque"
                >
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={String(loc.id)}>
                      {loc.name || (loc.isVirtual ? "Teleconsulta" : loc.address)}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSlotDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleCreateSlot}
              loading={slotSubmitting}
              disabled={locations.length > 0 && !slotForm.locationId}
            >
              {SCHEDULE_STRINGS.SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent size="sm" title={SCHEDULE_OVERRIDE_STRINGS.ADD}>
          <DialogBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Input
                type="date"
                label={SCHEDULE_OVERRIDE_STRINGS.DATE}
                min={today}
                value={overrideForm.date}
                onChange={(e) => setOverrideForm({ ...overrideForm, date: e.target.value })}
              />
              <Switch
                label={SCHEDULE_OVERRIDE_STRINGS.BLOCK_DAY}
                checked={overrideForm.isBlocked}
                onCheckedChange={(c) => setOverrideForm({ ...overrideForm, isBlocked: !!c })}
              />
              {!overrideForm.isBlocked && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Input
                    type="time"
                    label={SCHEDULE_STRINGS.START_TIME}
                    value={overrideForm.startTime}
                    onChange={(e) => setOverrideForm({ ...overrideForm, startTime: e.target.value })}
                  />
                  <Input
                    type="time"
                    label={SCHEDULE_STRINGS.END_TIME}
                    value={overrideForm.endTime}
                    onChange={(e) => setOverrideForm({ ...overrideForm, endTime: e.target.value })}
                  />
                </div>
              )}
              <Input
                label={SCHEDULE_OVERRIDE_STRINGS.REASON}
                placeholder={SCHEDULE_OVERRIDE_STRINGS.REASON_PLACEHOLDER}
                value={overrideForm.reason}
                onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverrideDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleCreateOverride}
              loading={overrideSubmitting}
              disabled={!overrideForm.date}
            >
              {SCHEDULE_OVERRIDE_STRINGS.SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
